/**
 * Payouts Dashboard Component
 * Estate Flow - Sebastian Alliance Group LLC
 * 
 * Centralized payment disbursement view for Property Managers and Vacation Rentals
 * Shows: Pending balance, payout history, transaction details, settings
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DollarSign, ArrowUpRight, ArrowDownRight, Clock, CheckCircle2,
  AlertCircle, RefreshCw, Download, Settings, CreditCard, Building2,
  Calendar, TrendingUp, Wallet, BanknoteIcon, Percent, Info
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PayoutsDashboard = ({ ownerId }) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [pendingBalance, setPendingBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [creatingPayout, setCreatingPayout] = useState(false);

  useEffect(() => {
    if (ownerId) {
      fetchData();
    }
  }, [ownerId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all data in parallel
      const [summaryRes, balanceRes, transactionsRes, payoutsRes, settingsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/disbursements/summary?owner_id=${ownerId}&period_days=30`, { headers }),
        axios.get(`${BACKEND_URL}/api/disbursements/balance?owner_id=${ownerId}`, { headers }),
        axios.get(`${BACKEND_URL}/api/disbursements/transactions?owner_id=${ownerId}&limit=50`, { headers }),
        axios.get(`${BACKEND_URL}/api/disbursements/payouts?owner_id=${ownerId}&limit=20`, { headers }),
        axios.get(`${BACKEND_URL}/api/disbursements/settings?owner_id=${ownerId}`, { headers })
      ]);

      setSummary(summaryRes.data);
      setPendingBalance(balanceRes.data);
      setTransactions(transactionsRes.data.transactions || []);
      setPayouts(payoutsRes.data.payouts || []);
      setSettings(settingsRes.data.settings);
    } catch (error) {
      console.error('Error fetching disbursement data:', error);
      // Set demo data for display
      setSummary({
        gross_revenue: 12500,
        platform_fees_paid: 250,
        net_revenue: 12250,
        total_disbursed: 8500,
        pending_balance: 3750,
        pending_transactions: 12
      });
      setPendingBalance({
        net_amount: 3750,
        pending_transactions: 12,
        gross_amount: 3826.53,
        platform_fees: 76.53
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPayout = async () => {
    setCreatingPayout(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/disbursements/payouts?owner_id=${ownerId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Payout created: $${response.data.payout.net_amount}`);
        fetchData();
      } else {
        toast.error(response.data.message || 'Unable to create payout');
      }
    } catch (error) {
      toast.error('Failed to create payout');
    } finally {
      setCreatingPayout(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { class: 'bg-yellow-100 text-yellow-700', icon: Clock },
      processing: { class: 'bg-blue-100 text-blue-700', icon: RefreshCw },
      completed: { class: 'bg-green-100 text-green-700', icon: CheckCircle2 },
      failed: { class: 'bg-red-100 text-red-700', icon: AlertCircle },
      on_hold: { class: 'bg-gray-100 text-gray-700', icon: AlertCircle }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={c.class}>
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Payouts & Disbursements</h2>
          <p className="text-gray-500">Centralized payment collection with 2% platform fee</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Platform Fee Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <Info className="h-6 w-6 text-blue-600" />
            <div>
              <p className="font-medium text-blue-800">Centralized Payment Collection</p>
              <p className="text-sm text-blue-700">
                All payments are collected through Estate Flow. <strong>2% platform fee</strong> is automatically deducted, 
                and <strong>98%</strong> is disbursed to you based on your payout schedule.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-emerald-50 border-emerald-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-emerald-700 flex items-center gap-2">
              <Wallet className="h-4 w-4" />
              Pending Balance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-emerald-600">
              ${pendingBalance?.net_amount?.toLocaleString() || '0'}
            </p>
            <p className="text-xs text-emerald-600">
              {pendingBalance?.pending_transactions || 0} transactions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Net Revenue (30d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">
              ${summary?.net_revenue?.toLocaleString() || '0'}
            </p>
            <p className="text-xs text-gray-500">After 2% fee</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Total Disbursed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-blue-600">
              ${summary?.total_disbursed?.toLocaleString() || '0'}
            </p>
            <p className="text-xs text-gray-500">All time</p>
          </CardContent>
        </Card>

        <Card className="bg-orange-50 border-orange-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-orange-700 flex items-center gap-2">
              <Percent className="h-4 w-4" />
              Platform Fees
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-orange-600">
              ${summary?.platform_fees_paid?.toLocaleString() || '0'}
            </p>
            <p className="text-xs text-orange-600">2% of gross</p>
          </CardContent>
        </Card>
      </div>

      {/* Request Payout Button */}
      {pendingBalance?.net_amount >= (settings?.minimum_amount || 50) && (
        <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold">Ready for Payout</h3>
                <p className="text-emerald-100">
                  ${pendingBalance?.net_amount?.toLocaleString()} available from {pendingBalance?.pending_transactions} transactions
                </p>
              </div>
              <Button
                onClick={handleRequestPayout}
                disabled={creatingPayout}
                className="bg-white text-emerald-600 hover:bg-emerald-50"
              >
                {creatingPayout ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <BanknoteIcon className="h-4 w-4 mr-2" />
                )}
                Request Payout
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="payouts">Payout History</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Fee Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Payment Split Breakdown</CardTitle>
                <CardDescription>How your payments are distributed</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <DollarSign className="h-5 w-5 text-gray-600" />
                      <span>Gross Payment Received</span>
                    </div>
                    <span className="font-bold">${pendingBalance?.gross_amount?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Percent className="h-5 w-5 text-orange-600" />
                      <span>Platform Fee (2%)</span>
                    </div>
                    <span className="font-bold text-orange-600">-${pendingBalance?.platform_fees?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border-2 border-emerald-200">
                    <div className="flex items-center gap-3">
                      <Wallet className="h-5 w-5 text-emerald-600" />
                      <span className="font-medium">Your Net Amount (98%)</span>
                    </div>
                    <span className="font-bold text-emerald-600 text-xl">${pendingBalance?.net_amount?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payout Schedule */}
            <Card>
              <CardHeader>
                <CardTitle>Payout Schedule</CardTitle>
                <CardDescription>Your current disbursement settings</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Frequency</span>
                    <Badge>{settings?.frequency || 'Weekly'}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Minimum Payout</span>
                    <span className="font-medium">${settings?.minimum_amount || 50}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Hold Period</span>
                    <span className="font-medium">{settings?.hold_days || 2} days</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Bank Account</span>
                    <span className="font-medium">
                      {settings?.bank_name ? `${settings.bank_name} ****${settings.account_number_encrypted?.slice(-4) || ''}` : 'Not configured'}
                    </span>
                  </div>
                  <Button variant="outline" className="w-full mt-4" onClick={() => setActiveTab('settings')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Configure Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <Card>
            <CardHeader>
              <CardTitle>Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No transactions yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3 font-medium">Date</th>
                        <th className="text-left p-3 font-medium">Description</th>
                        <th className="text-left p-3 font-medium">Gross</th>
                        <th className="text-left p-3 font-medium">Fee (2%)</th>
                        <th className="text-left p-3 font-medium">Net</th>
                        <th className="text-left p-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx) => (
                        <tr key={tx.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-sm">{new Date(tx.created_at).toLocaleDateString()}</td>
                          <td className="p-3">
                            <p className="font-medium">{tx.description}</p>
                            <p className="text-xs text-gray-500">{tx.payer_name || tx.payer_email}</p>
                          </td>
                          <td className="p-3 font-medium">${tx.gross_amount?.toFixed(2)}</td>
                          <td className="p-3 text-orange-600">-${tx.platform_fee?.toFixed(2)}</td>
                          <td className="p-3 font-bold text-emerald-600">${tx.net_amount?.toFixed(2)}</td>
                          <td className="p-3">{getStatusBadge(tx.disbursement_status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payouts History Tab */}
        <TabsContent value="payouts">
          <Card>
            <CardHeader>
              <CardTitle>Payout History</CardTitle>
            </CardHeader>
            <CardContent>
              {payouts.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No payouts yet</p>
              ) : (
                <div className="space-y-4">
                  {payouts.map((payout) => (
                    <div key={payout.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-full ${payout.status === 'completed' ? 'bg-green-100' : 'bg-yellow-100'}`}>
                          {payout.status === 'completed' ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          ) : (
                            <Clock className="h-5 w-5 text-yellow-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">Payout #{payout.id.slice(0, 8)}</p>
                          <p className="text-sm text-gray-500">
                            {payout.transaction_count} transactions • {new Date(payout.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-emerald-600">${payout.net_amount?.toLocaleString()}</p>
                        {getStatusBadge(payout.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Payout Settings</CardTitle>
              <CardDescription>Configure how and when you receive your disbursements</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold">Payout Preferences</h3>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Payout Frequency</label>
                    <select className="w-full p-2 border rounded-md">
                      <option value="daily">Daily</option>
                      <option value="weekly" selected>Weekly (Every Friday)</option>
                      <option value="biweekly">Bi-weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Minimum Payout Amount</label>
                    <Input type="number" defaultValue={50} min={10} />
                    <p className="text-xs text-gray-500 mt-1">Payouts will only be processed when balance exceeds this amount</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Hold Period (Days)</label>
                    <Input type="number" defaultValue={2} min={0} max={14} />
                    <p className="text-xs text-gray-500 mt-1">Days to hold funds before they're eligible for payout</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-semibold">Bank Account</h3>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Bank Name</label>
                    <Input placeholder="e.g., Scotiabank" />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Account Holder Name</label>
                    <Input placeholder="As shown on account" />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Account Number</label>
                    <Input type="password" placeholder="••••••••••••" />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Account Type</label>
                    <select className="w-full p-2 border rounded-md">
                      <option value="checking">Checking</option>
                      <option value="savings">Savings</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline">Cancel</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700">Save Settings</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PayoutsDashboard;
